import { gql } from "@apollo/client";

export const User = {
  fragments: {
    UserBasicData: gql`
      fragment UserBasicData on User {
        _id
        name
        email
        info
        access
        userCredentialsSent
        isOem
        emailNotification
        role
        roleId
        roleName
        skills {
          _id
        }
        hasAiAssistantAccess
        notification {
          email {
            onNewTicketAssigned
            onAssignedTicketInternalNotePost
            onAssignedWorkOrderUpdate
            onAddedAsWorkOrderFollower
            onMentionedInWorkOrderInternalNote
            onMessageOnAssignedWorkOrder
            onEmailReceivedOnWorkOrder
            onAssignedWorkOrderReminder {
              enabled
              daysBefore
            }
            onWorkOrderDueDateReminder {
              enabled
              daysBefore
            }
          }
        }
        firstName
        lastName
        department
        function
        phone
        mobile
        deleted
        productAccess
        teams {
          _id
          name
          teamColor
        }
      }
    `,
    UserAuthData: gql`
      fragment UserAuthData on User {
        _id
        fileImporter {
          _id
          user
          importers {
            fields
            settings
            meta {
              templateId
              templateSection
            }
          }
        }
        name
        email
        emailSignature
        info
        access
        userCredentialsSent
        isOem
        emailNotification
        role
        roleId
        roleName
        permissions
        notification {
          email {
            onNewTicketAssigned
            onAssignedTicketInternalNotePost
            onAssignedWorkOrderUpdate
            onAddedAsWorkOrderFollower
            onMentionedInWorkOrderInternalNote
            onMessageOnAssignedWorkOrder
            onEmailReceivedOnWorkOrder
            onAssignedWorkOrderReminder {
              enabled
              daysBefore
            }
            onWorkOrderDueDateReminder {
              enabled
              daysBefore
            }
          }
        }
        productAccess
        boards {
          boardFor
          customFields {
            _id
            label
            options {
              value
              color
              _id
            }
          }
        }
        hasAiAssistantAccess
        calendarSyncAccountLinked
        calendarSyncAccountStatus
        linkedCalendarSyncEmailAddress
        calendarSyncAccountNeedsReconnect
        firstSignInRedirectUrl
        dateFormat
        timeFormat
        workingHours {
          dayOfWeek
          enabled
          from
          to
        }
        hasPersonalWorkingHours
        portals {
          _id
          id
          name
          portalName
          linkedConnection
          brandLogo
          portalHierarchy
          allowSubPortalCreation
        }
      }
    `,
  },
};

export const WorkOrderType = {
  fragments: {
    WorkOrderTypeData: gql`
      fragment WorkOrderTypeData on TicketType {
        _id
        name
        color
        icon
        isInternal
        isSystem
        partsConfig {
          show
          isRequired
          fieldName
        }
        descriptionConfig {
          show
          isRequired
          fieldName
        }
        customFields {
          _id
          isRequired
          description
          customAdditionalField {
            _id
            type
            label
            fieldType
            options {
              _id
              value
              color
            }
          }
        }
      }
    `,
  },
};

export const Oem = {
  fragments: {
    AiAssistantConfiguration: gql`
      fragment AiAssistantConfiguration on Oem {
        aiAssistantConfiguration {
          allowedOcrScans
          allowedQueries
          allowedRecordingSeconds
          allowedStorage
          allowedUsers
          autoApproveOCR
        }
      }
    `,
    AiAssistantUsage: gql`
      fragment AiAssistantUsage on Oem {
        aiAssistantUsage {
          consumedOcrScans
          consumedUsers
        }
      }
    `,
    OemBasicData: gql`
      fragment OemBasicData on Oem {
        _id
        logo
        thumbnail
        backgroundColor
        brandLogo
        heading
        subHeading
        paragraph
        textColor
        name
        headline
        location
        currency
        website
        description
        coverPhoto
        urlOemFacility
        paidFeatures
        installedProducts {
          type
          tier
        }
        slug
        allowFollowersMyWorkOrders
        allowAssigneesAcrossTeams
        allowTechnicianToAssignWorkOrders
        productCatalogEnabled
        autoLinkContactsByDomain
        statuses {
          _id
          label
          color
        }
        supportTicketStatuses {
          _id
          label
          color
        }
        skills {
          _id
          name
        }
        ticketTypes {
          ...WorkOrderTypeData
        }
        supportTicketTypes {
          ...WorkOrderTypeData
        }
        notification {
          email {
            notifyOnWorkOrderCreation
            workOrderCreationNotifyTo {
              user {
                _id
                name
                email
              }
              workOrderTypes
            }
            notifyOnMaintenanceWorkOrderCreation
            maintenanceWorkOrderCreationNotifyTo {
              _id
              name
            }
            notifyOnMessageOnUnassignedWorkOrder
            messageOnUnassignedWorkOrderNotifyTo {
              _id
              name
            }
            notifyOnWorkOrderDueDateReminder
            workOrderDueDateReminderNotifyTo {
              _id
              name
              email
            }
            workOrderDueDateReminderDaysBefore
            notifyOnSupportTicketCreation
            supportTicketNotifyTo {
              user {
                _id
                name
                email
              }
              supportTicketTypes
            }
            notifyOnExternalMessageOnUnassignedSupportTicket
            externalMessageOnUnassignedSupportTicketNotifyTo {
              _id
              name
              email
            }
          }
        }
        defaultProcedureHeader {
          url
          name
        }
        defaultProcedureFooter {
          url
          name
        }
        procedurePreferences {
          showHeaderOnEveryPage
        }
        archiveSettings {
          autoArchiveDelayDays
        }
        businessHours {
          dayOfWeek
          enabled
          from
          to
        }
      }
      ${WorkOrderType.fragments.WorkOrderTypeData}
    `,
  },
};

export const CustomizeField = {
  fragments: {
    AdditionFieldData: gql`
      fragment AdditionFieldData on AdditionalField {
        _id
        type
        slug
        label
        fieldType
        isAdditionalField
        enabled
      }
    `,

    CustomAdditionFieldData: gql`
      fragment CustomAdditionFieldData on CustomAdditionalField {
        _id
        description
        type
        slug
        label
        fieldType
        isAdditionalField
        enabled
        created_at
        order
        visibilityScope
        oem {
          _id
          name
        }
        options {
          _id
          color
          description
          value
        }
      }
    `,
  },
};

export const Customer = {
  fragments: {
    CustomerBasicData: gql`
      fragment CustomerBasicData on Customer {
        _id
        name
        isQRCodeEnabled
        linkedOrg
        facilityId
        accessible
        facilityIdentifier
        createdAt
        updatedAt
        createdBy
        customFields {
          _id
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
        teams {
          _id
          name
          teamColor
        }
        parents {
          id
          name
          facilityIdentifier
        }
        hierarchy {
          id
          name
          facilityIdentifier
        }
        totalChildrenCount
        childrenCount
      }
      ${CustomizeField.fragments.CustomAdditionFieldData}
    `,
    CustomerFullData: gql`
      fragment CustomerFullData on Customer {
        _id
        facilityId
        name
        isQRCodeEnabled
        qrCodeAccess
        linkedOrg
        isMachineDocumentationEnabled
        isPreventiveMaintenanceEventsEnabled
        generalAccessUrl
        totalMachines
        createdBy
        createdAt
        description
        website
        type
        showParent
        customFields {
          _id
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
        teams {
          _id
          name
          teamColor
        }
        assetAccess {
          documentation
          parts
          preventiveMaintenance
          qrCodes
          history
        }
        documentFolders {
          internalId
          externalId
        }
        parents {
          id
          name
          facilityIdentifier
          hasPortal
        }
        accessibleParents {
          id
          name
          facilityIdentifier
          hasPortal
        }
        hierarchy {
          id
          name
          facilityIdentifier
        }
        totalChildrenCount
        childrenCount
        hasCustomerPortal
        parentsWithPortalCount
      }
      ${CustomizeField.fragments.CustomAdditionFieldData}
    `,
  },
};

export const Resource = {
  fragments: {
    ResourceData: gql`
      fragment ResourceData on Resource {
        _id
        name
        resourceId
        ticketsCount
        type
      }
    `,
    ResourceListData: gql`
      fragment ResourceListData on Resource {
        _id
        name
        resourceId
        ticketsCount
        type
      }
    `,
  },
};

export const TicketScheduleDraft = {
  fragments: {
    TicketScheduleDraftData: gql`
      fragment TicketScheduleDraftData on TicketScheduleDraft {
        _id
        draft {
          operationType
          ticket {
            id
            assignees
            resources
            schedule {
              startTime
              endTime
              startTimezone
              timezone
              endTimezone
              isAllDay
              isTimezoneEnabled
            }
            status
            dueDate
          }
          unavailable {
            id
            user
            schedule {
              startTime
              endTime
              startTimezone
              endTimezone
              isAllDay
              isTimezoneEnabled
            }
            scheduleType
            toDelete
          }
        }
        version
      }
    `,
  },
};

export const UserSchedule = {
  fragments: {
    UserScheduleData: gql`
      fragment UserScheduleData on UserSchedule {
        _id
        schedule {
          startTime
          endTime
          startTimezone
          endTimezone
          isAllDay
          isTimezoneEnabled
        }
        scheduleType
        user
        uuid
      }
    `,
  },
};

export const AiNote = {
  fragments: {
    AiNoteBasicData: gql`
      fragment AiNoteBasicData on AiNote {
        _id
        audioDuration
        title
        createdAt
        createdBy {
          _id
          name
        }
        transcriptionStatus
      }
    `,
    AiNoteWorkOrderData: gql`
      fragment AiNoteWorkOrderData on AiNote {
        _id
        audioDuration
        createdAt
        createdBy {
          _id
          name
        }
        languageCode
        summary
        title
        transcriptionStatus
      }
    `,
    AiNoteData: gql`
      fragment AiNoteData on AiNote {
        _id
        audioUrl
        createdAt
        createdBy {
          _id
          name
        }
        publishedMachines {
          machine {
            _id
            image
            name
            serialNumber
          }
          machineHistoryNoteId
          publishedAt
          publishedBy
        }
        publishedTickets {
          publishedAt
          publishedBy
          ticket {
            _id
            ticketId
            title
          }
        }
        summary
        title
        transcript
        transcriptionStatus
      }
    `,
    AiNotePublicData: gql`
      fragment AiNotePublicData on AiNote {
        _id
        audioUrl
        createdAt
        createdBy {
          _id
          name
        }
        summary
        title
        transcript
      }
    `,
  },
};

export const Schedule = {
  fragments: {
    TicketScheduleData: gql`
      fragment TicketScheduleData on Ticket {
        schedule {
          startTime
          endTime
          timezone
          isAllDay
          isTimezoneEnabled
        }
      }
    `,
  },
};

export const Document = {
  fragments: {
    DocumentData: gql`
      fragment DocumentData on Document {
        _id
        aiGeneratedTitle
        authors {
          name
          confidence
        }
        boxDocumentId
        fileUrl
        filePDFUrl
        contributors {
          name
          confidence
        }
        customFields {
          fieldId {
            _id
            label
            enabled
            fieldType
            options {
              _id
              value
              color
            }
          }
          reasonReferenceMap
          value
          values
        }
        date
        editors {
          name
          confidence
        }
        language
        size
        status
        title
      }
    `,
  },
};

export const Asset = {
  fragments: {
    AssetData: gql`
      fragment AssetData on Asset {
        _id
        customer {
          _id
          name
          facilityId
        }
        slug
        name
        serialNumber
        image
        thumbnail
        description
        createdBy
        oem {
          _id
          name
          slug
        }
        template {
          _id
          title
          image
          thumbnail
        }
        detachedFromTemplate {
          image
          description
          inventoryParts
          procedures
        }
      }
    `,
    AssetFullData: gql`
      fragment AssetFullData on Asset {
        _id
        slug
        oem {
          _id
          name
          slug
          statuses {
            _id
            label
            color
          }
          paidFeatures
          installedProducts {
            type
            tier
          }
          logo
          thumbnail
          brandLogo
          ticketTypes {
            _id
            name
            isInternal
            isSystem
          }
        }
        customer {
          _id
          name
          facilityId
          linkedOrg
        }
        name
        detachedFromTemplate {
          image
          description
          inventoryParts
          documentation
          procedures
        }
        folderId
        serialNumber
        isBoxFoldersDisabled
        isQRCodeEnabled
        generalAccessUrl
        customFields {
          _id
          fieldId {
            _id
            type
            label
            fieldType
            isAdditionalField
            visibilityScope
            enabled
            created_at
            order
            options {
              _id
              value
              color
            }
          }
          value
          values
        }
        image
        thumbnail
        description
        template {
          _id
          title
          image
          thumbnail
          description
          documentFolders {
            internalId
            externalId
          }
        }
        createdAt
        updatedAt
        createdBy
        documentFolders {
          internalId
          externalId
        }
        teams {
          _id
          name
          teamColor
        }
        aiAssistant {
          _id
          assistantName
        }
        assetType
        hierarchy {
          id
          name
          serialNumber
        }
        childrenCount
        totalChildrenCount
        showParent
        procedures {
          procedure {
            _id
            name
            createdAt
            childrenCount
            isInternal
          }
          inheritedFromTemplate
        }
      }
    `,
    AssetPartsData: gql`
      fragment AssetPartsData on Asset {
        _id
        inventoryParts {
          part {
            _id
            name
            visibilityScope
            articleNumber
            totalStock
            consumedStock
            availableStock
            totalCost
            description
            image
            thumbnail
            oem {
              _id
              name
              paidFeatures
            }
            customFields {
              _id
              fieldId {
                _id
                type
                label
                fieldType
                isAdditionalField
                enabled
                created_at
                order
                options {
                  _id
                  value
                  color
                }
              }
              value
              values
            }
          }
          inheritedFromTemplate
        }
      }
    `,
    AssetListData: gql`
      fragment AssetListData on Asset {
        _id
        name
        serialNumber
        image
        createdAt
        updatedAt
        thumbnail
        generalAccessUrl
        isQRCodeEnabled
        teams {
          _id
          name
          teamColor
        }
        parentTeams {
          _id
        }
        customer {
          _id
          name
        }
        customFields {
          _id
          fieldId {
            _id
            type
            label
            fieldType
            isAdditionalField
            enabled
            created_at
            order
            options {
              _id
              value
              color
            }
          }
          value
          values
        }
        createdBy
        template {
          _id
          title
          image
          thumbnail
        }
        oem {
          _id
          logo
          name
          brandLogo
          paidFeatures
          ticketTypes {
            ...WorkOrderTypeData
          }
          thumbnail
        }
        assetType
        hierarchy {
          id
          name
        }
        childrenCount
        totalChildrenCount
      }
      ${WorkOrderType.fragments.WorkOrderTypeData}
    `,
    AssetSearchListData: gql`
      fragment AssetSearchListData on Asset {
        _id
        name
        hierarchy {
          id
          name
        }
        serialNumber
        image
      }
    `,
  },
};

export const Ticket = {
  fragments: {
    TicketData: gql`
      fragment TicketData on Ticket {
        _id
        ticketId
        title
        description
        status
        url
      }
    `,
    TicketListData: gql`
      fragment TicketListData on Ticket {
        _id
        ticketId
        dueDate
        title
        status
        oem {
          brandLogo
          statuses {
            _id
            label
            color
          }
        }
        parentWorkOrder {
          _id
          title
          ticketId
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
          }
        }
        ticketType {
          _id
          name
          icon
          color
          isInternal
          isSystem
        }
        createdAt
        createdBy {
          _id
          name
        }
        updatedAt
        maintenanceOn
        hasOriginalEventBeenDeleted
        assignees {
          _id
          name
        }
        followers {
          _id
        }
        machine {
          _id
          name
          serialNumber
          isDeleted
        }
        assets {
          _id
          name
          serialNumber
          isDeleted
        }
        subWorkOrders {
          _id
        }
        facility {
          _id
          name
          isDeleted
          customFields {
            _id
            fieldId {
              _id
              slug
            }
            value
            values
          }
        }
        ticketChatChannels
        ticketInternalNotesChatChannels
        customFields {
          fieldId {
            _id
          }
          value
          values
        }
        teams {
          _id
          name
          teamColor
        }
        procedures {
          procedure {
            _id
          }
        }
        companyContacts {
          _id
          name
          email
          accessStatus
        }
        resources {
          _id
        }
        archived
        archivedAt
        archivedBy {
          _id
          name
        }
        ...TicketScheduleData
      }
      ${Schedule.fragments.TicketScheduleData}
    `,

    TicketSingleData: gql`
      fragment TicketSingleData on Ticket {
        _id
        ticketId
        dueDate
        ticketType {
          _id
          name
          icon
          color
          isInternal
          isSystem
          descriptionConfig {
            show
            isRequired
            fieldName
          }
        }
        parentWorkOrder {
          _id
          title
          ticketId
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
            descriptionConfig {
              show
              isRequired
              fieldName
            }
          }
          assets {
            _id
          }
          facility {
            _id
            name
            facilityId
            facilityIdentifier
            isDeleted
          }
        }
        subWorkOrders {
          _id
          ticketId
          ticketType {
            _id
            name
            icon
            color
            isInternal
            isSystem
            descriptionConfig {
              show
              isRequired
              fieldName
            }
          }
          title
          status
          facility {
            _id
            name
          }
          assets {
            _id
            name
            serialNumber
          }
          teams {
            _id
          }
        }
        oem {
          website
          brandLogo
          paidFeatures
          statuses {
            _id
            label
            color
          }
          paidFeatures
          installedProducts {
            type
            tier
          }
        }
        linkedTickets {
          ticket {
            _id
            ticketId
            ticketType {
              _id
              name
              icon
              color
              isInternal
              isSystem
              descriptionConfig {
                show
                isRequired
                fieldName
              }
            }
            title
            status
            facility {
              name
            }
            assets {
              _id
              name
              serialNumber
            }
            teams {
              _id
            }
          }
          linkedBy {
            _id
          }
          linkedAt
        }
        linkedSupportTickets {
          supportTicket {
            _id
            title
            supportTicketId
            type {
              icon
              color
              isInternal
            }
          }
          linkedBy {
            _id
          }
          linkedAt
        }
        title
        description
        solution
        status
        notes
        url
        createdAt
        createdBy {
          _id
          name
        }
        createdByContact {
          _id
          name
        }
        updatedAt
        lastUpdatedAt
        maintenanceOn
        hasOriginalEventBeenDeleted
        teams {
          _id
          name
          teamColor
          members {
            _id
            name
          }
        }
        assignees {
          ...UserBasicData
        }
        followers {
          ...UserBasicData
        }
        companyContacts {
          _id
          name
          email
          accessStatus
        }
        machine {
          ...AssetFullData
        }
        assets {
          ...AssetFullData
          isDeleted
        }
        facility {
          ...CustomerBasicData
          isDeleted
        }
        unaffiliatedAssetLinkedTo {
          kind
          documentId
        }
        customFields {
          _id
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
        ticketChatChannels
        ticketInternalNotesChatChannels
        inventoryParts {
          quantity
          addedBy {
            _id
            role
            oem {
              _id
            }
          }
          asset {
            _id
            name
            serialNumber
          }
          isAccessibleByTeam
          part {
            _id
            name
            visibilityScope
            articleNumber
            totalStock
            consumedStock
            availableStock
            totalCost
            description
            image
            thumbnail
            oem {
              _id
              thumbnail
              paidFeatures
            }
          }
          stockOperation {
            _id
            allocationStatus
          }
        }
        attachments {
          name
          type
          url
          size
          createdAt
          _id
        }
        aiNotes {
          ...AiNoteWorkOrderData
        }
        timeTrackerLogs {
          _id
          description
          startDateTime
          endDateTime
          isBillable
          timeElapsedInSeconds
          ticketTag {
            _id
            label
            color
          }
          createdBy {
            _id
            name
          }
          customFields {
            fieldId {
              _id
              label
              fieldType
            }
            value
            values
          }
        }
        totalTimeLoggedInSeconds
        procedures {
          procedure {
            _id
            name
            createdAt
            createdBy {
              _id
            }
            pdfUrl
            state
            procedureId
            isInternal
            children {
              _id
            }
            teams {
              _id
              oem {
                _id
                name
              }
              name
              description
              teamColor
            }
          }
          isAccessible
          addedBy {
            name
            _id
          }
        }
        resources {
          _id
        }
        archived
        archivedAt
        archivedBy {
          _id
          name
        }
        ...TicketScheduleData
      }
      ${AiNote.fragments.AiNoteWorkOrderData}
      ${User.fragments.UserBasicData}
      ${Asset.fragments.AssetFullData}
      ${Customer.fragments.CustomerBasicData}
      ${CustomizeField.fragments.CustomAdditionFieldData}
      ${Schedule.fragments.TicketScheduleData}
    `,
  },
};

export const CallInfo = {
  fragments: {
    CallInfoData: gql`
      fragment CallInfoData on CallInfo {
        _id
        uid
        token
        channel
        caller
        receiver
      }
    `,
  },
};

export const PreventiveMaintenance = {
  fragments: {
    PreventiveEventData: gql`
      fragment PreventiveEventData on PreventiveMaintenance {
        _id
        title
        startDate
        eventDate
        frequency
        repeatInNumber
        repeatIn
        ticketCreationNumber
        ticketCreationIn
        estimatedDurationNumber
        estimatedDurationIn
        repetitionEnd
        repetitionEndValueOccurrences
        repetitionEndValueDate
        lastCompleted
        allOccurrencesDone
        endsOn
        description
        nextCreationDate
        occurrenceSchedule {
          past {
            triggerDate
            dueDate
            scheduledStart
            scheduledEnd
            canCreateWorkOrder
            workOrderGenerationPending
            workOrder {
              _id
              ticketId
              isDeleted
            }
          }
          upcoming {
            triggerDate
            dueDate
            scheduledStart
            scheduledEnd
            canCreateWorkOrder
            workOrderGenerationPending
            workOrder {
              _id
              ticketId
              isDeleted
            }
          }
        }
        procedures {
          procedure {
            _id
            name
          }
        }
        inventoryParts {
          part {
            _id
            name
            visibilityScope
            image
          }
          quantity
        }
        createdBy {
          _id
        }
        oem {
          _id
        }
        machine {
          _id
          customer {
            _id
            name
          }
        }
      }
    `,
    PreventiveEventDetailedData: gql`
      fragment PreventiveEventDetailedData on PreventiveMaintenance {
        _id
        title
        startDate
        eventDate
        frequency
        repeatInNumber
        repeatIn
        repetitionEnd
        repetitionEndValueOccurrences
        repetitionEndValueDate
        endsOn
        ticketCreationNumber
        ticketCreationIn
        estimatedDurationNumber
        estimatedDurationIn
        description
        nextCreationDate
        occurrenceSchedule {
          past {
            triggerDate
            dueDate
            scheduledStart
            scheduledEnd
            canCreateWorkOrder
            workOrderGenerationPending
            workOrder {
              _id
              ticketId
              isDeleted
            }
          }
          upcoming {
            triggerDate
            dueDate
            scheduledStart
            scheduledEnd
            canCreateWorkOrder
            workOrderGenerationPending
            workOrder {
              _id
              ticketId
              isDeleted
            }
          }
        }
        createdBy {
          _id
          name
          email
        }
        oem {
          _id
        }
        machine {
          _id
          name
          customer {
            _id
            name
          }
        }
        procedures {
          procedure {
            _id
            name
          }
          addedBy {
            name
            _id
          }
        }
        inventoryParts {
          part {
            _id
            name
            visibilityScope
            articleNumber
            totalStock
            consumedStock
            availableStock
            totalCost
            description
            image
            thumbnail
          }
          quantity
        }
      }
    `,
  },
};

export const InventoryPart = {
  fragments: {
    InventoryPartBasicData: gql`
      fragment InventoryPartBasicData on InventoryPart {
        _id
        name
        visibilityScope
        articleNumber
        description
        totalStock
        consumedStock
        availableStock
        totalCost
        price
        image
        thumbnail
        createdBy
        createdAt
        oem {
          ...OemBasicData
        }
        customFields {
          _id
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
      }
      ${Oem.fragments.OemBasicData}
      ${CustomizeField.fragments.CustomAdditionFieldData}
    `,
    InventoryPartFullData: gql`
      fragment InventoryPartFullData on InventoryPart {
        _id
        name
        visibilityScope
        articleNumber
        totalStock
        consumedStock
        availableStock
        totalCost
        price
        description
        image
        thumbnail
        createdBy
        oem {
          ...OemBasicData
        }
        customFields {
          _id
          fieldId {
            ...CustomAdditionFieldData
          }
          value
          values
        }
        documentFolders {
          internalId
          externalId
        }
        accessToken
      }
      ${Oem.fragments.OemBasicData}
      ${CustomizeField.fragments.CustomAdditionFieldData}
    `,
  },
};

export const TimeTracker = {
  fragments: {
    TimeTrackerBasicData: gql`
      fragment TimeTrackerBasicData on TimeTracker {
        _id
        type
        slug
        label
        fieldType
        color
      }
    `,
  },
};

const ProcedureTemplateChildrenNode = {
  fragments: {
    ProcedureTemplateChildren: gql`
      fragment ProcedureTemplateChildren on ProcedureTemplateNode {
        _id
        name
        type
        description
        isRequired
        attachments {
          _id
          name
          type
          url
          size
        }
        options {
          _id
          name
        }
        value
        allowExtraRows
        tableOption {
          _id
          columns {
            _id
            heading
            width
            uniqueId
          }
          rowCount
        }
      }
    `,
  },
};
const ProcedureInstanceChildrenNode = {
  fragments: {
    ProcedureInstanceChildren: gql`
      fragment ProcedureInstanceChildren on ProcedureNode {
        _id
        name
        type
        description
        isRequired
        value
        allowExtraRows
        attachments {
          _id
          name
          type
          url
          size
        }
        options {
          _id
          name
        }
        tableOption {
          _id
          columns {
            _id
            heading
            width
            uniqueId
          }
          rowCount
        }
      }
    `,
  },
};
export const ProcedureTemplate = {
  fragments: {
    ProcedureTemplateFullData: gql`
      fragment ProcedureTemplateFullData on ProcedureTemplate {
        _id
        name
        description
        createdAt
        updatedAt
        pageHeader {
          name
          size
          type
          url
        }
        pageFooter {
          name
          size
          type
          url
        }
        createdBy {
          _id
          name
        }
        signatures {
          _id
          signatoryTitle
        }
        children {
          ...ProcedureTemplateChildren
          children {
            ...ProcedureTemplateChildren
            children {
              ...ProcedureTemplateChildren
            }
          }
        }
        allowUsersToAddMoreSignatures
        childrenCount
        isInternal
        teams {
          _id
          oem {
            _id
            name
          }
          name
          description
          teamColor
        }
        headerFields {
          id {
            _id
            displayName
            hidden
          }
          connectionId {
            _id
            displayName
            hidden
          }
          connectionName {
            _id
            displayName
            hidden
          }
          connectionAddress {
            _id
            displayName
            hidden
          }
          assets {
            _id
            displayName
            hidden
          }
          workOrderId {
            _id
            displayName
            hidden
          }
          workOrderTitle {
            _id
            displayName
            hidden
          }
          workOrderCreationDate {
            _id
            displayName
            hidden
          }
          customFields {
            label
            fieldId
          }
        }
      }
      ${ProcedureTemplateChildrenNode.fragments.ProcedureTemplateChildren}
    `,
  },
};
export const ProcedureInstance = {
  fragments: {
    ProcedureInstanceFullData: gql`
      fragment ProcedureInstanceFullData on Procedure {
        _id
        name
        state
        description
        createdAt
        updatedAt
        pdfUrl
        procedureId
        isInternal
        pageHeader {
          name
          size
          type
          url
        }
        pageFooter {
          name
          size
          type
          url
        }
        createdBy {
          _id
          name
        }
        signatures {
          _id
          signatoryTitle
          name
          date
          signatureUrl
          isAdditionalSignature
        }
        submittedBy {
          _id
          name
        }
        children {
          ...ProcedureInstanceChildren
          children {
            ...ProcedureInstanceChildren
            children {
              ...ProcedureInstanceChildren
            }
          }
        }
        allowUsersToAddMoreSignatures
        headerFields {
          id {
            _id
            displayName
            hidden
          }
          connectionId {
            _id
            displayName
            hidden
          }
          connectionName {
            _id
            displayName
            hidden
          }
          connectionAddress {
            _id
            displayName
            hidden
          }
          assets {
            _id
            displayName
            hidden
          }
          workOrderId {
            _id
            displayName
            hidden
          }
          workOrderTitle {
            _id
            displayName
            hidden
          }
          workOrderCreationDate {
            _id
            displayName
            hidden
          }
          customFields {
            fieldId
            label
            fieldType
            value
            values
          }
        }
      }
      ${ProcedureInstanceChildrenNode.fragments.ProcedureInstanceChildren}
    `,
  },
};

export const Team = {
  fragments: {
    TeamData: gql`
      fragment TeamData on Team {
        _id
        oem {
          _id
          name
        }
        name
        description
        teamColor
        disallowedTicketTypes {
          name
          color
          icon
          isInternal
          _id
        }
      }
    `,
  },
};

export const Reports = {
  fragments: {
    ReportData: gql`
      fragment ReportData on Report {
        _id
        title
        createdBy
        filters {
          field
          condition
          value {
            value
            label
          }
        }
        xAxis {
          value
          label
        }
        segment {
          value
          label
        }
        chartType
        table {
          value
          label
        }
        entity
      }
    `,
  },
};
export const MachineHistory = {
  fragments: {
    MachineHistoryData: gql`
      fragment MachineHistoryData on MachineHistory {
        year
        type
        resourceId
        resource
        note {
          message
          isInternal
          attachments {
            url
            type
            size
            name
            createdAt
          }
        }
        month
        machine
        machineDetails {
          name
          _id
          serialNumber
          isDeleted
        }
        _id
        createdAt
        createdBy {
          name
          _id
        }
        customer {
          _id
          name
          isDeleted
        }
        ticket {
          _id
          title
          ticketId
          status
          ticketType {
            isInternal
          }
        }
        ticketDetails {
          _id
          assets {
            _id
            name
            serialNumber
            isDeleted
          }
          subWorkOrders {
            _id
          }
        }
        supportTicketDetails {
          _id
          assets {
            _id
            name
            serialNumber
            isDeleted
          }
        }
        level
      }
    `,
  },
};

export const ConnectionHistory = {
  fragments: {
    ConnectionHistoryData: gql`
      fragment ConnectionHistoryData on ConnectionHistory {
        year
        type
        resourceId
        resource
        month
        machine
        _id
        createdAt
        createdBy {
          name
          _id
        }
        customer {
          _id
          name
        }
        ticket {
          _id
          title
          ticketId
          status
          ticketType {
            isInternal
          }
        }
      }
    `,
  },
};

export const Report = {
  fragment: {
    InputReportData: gql`
      fragment InputReportData on Report {
        _id
        title
        xAxis {
          value
          label
        }
        table {
          value
          label
        }
        chartType
        segment {
          value
          label
        }
        filters {
          filterType
          field
          condition
          value {
            value
            label
          }
        }
      }
    `,
  },
};

export const ActivityLog = {
  fragments: {
    ActivityLogData: gql`
      fragment ActivityLogData on ActivityLog {
        _id
        resource
        createdAt
        attribute
        actor
        action
        meta
      }
    `,
  },
};

export const AiAssistant = {
  BasicData: gql`
    fragment BasicData on AiAssistant {
      _id
      assistantName
      assistantType
      description
      machine {
        serialNumber
        name
        image
        detachedFromTemplate {
          documentation
        }
      }
      oem
      assistantType
      isSharedAssistant
      voiceQueryEnabled
      oemDetails {
        website
        name
        logo
        brandLogo
      }
      templateId
      template {
        title
        templateId
      }
      machineID
      createdBy
    }
  `,
};

export const AiAssistantChat = {
  fragments: {
    BasicData: gql`
      fragment BasicData on AiAssistantChat {
        _id
        createdAt
        title
        isSharedAssistantChat
        aiAssistant {
          _id
          assistantName
        }
      }
    `,
    FullData: gql`
      fragment AiAssistantChatData on AiAssistantChat {
        _id
        aiAssistant {
          _id
          assistantName
          isSharedAssistant
          voiceQueryEnabled
          oemDetails {
            name
            website
            logo
            brandLogo
          }
        }
        createdAt
        title
        messages {
          _id
          role
          content
          createdAt
          citations {
            _id
            documentChunkId
            documentId
            highlight
            pageNumber
            score
            snippet
            title
          }
        }
      }
    `,
  },
};

export const AssetTemplate = {
  fragments: {
    AssetTemplateData: gql`
      fragment AssetTemplateData on AssetTemplate {
        _id
        templateId
        title
        description
        image
        thumbnail
        createdBy
        visibility
        oem {
          ...OemBasicData
        }
        assets {
          _id
        }
      }
      ${Oem.fragments.OemBasicData}
    `,
    AssetTemplateFullData: gql`
      fragment AssetTemplateFullData on AssetTemplate {
        _id
        templateId
        title
        description
        image
        thumbnail
        createdBy
        createdAt
        updatedAt
        visibility
        oem {
          ...OemBasicData
        }
        procedures {
          procedureId
          addedBy {
            _id
          }
          procedure {
            _id
            name
          }
        }
        aiAssistant {
          _id
          assistantName
        }
        documentFolders {
          internalId
          externalId
        }
        inventoryParts {
          part {
            _id
            name
            articleNumber
            totalStock
            consumedStock
            availableStock
            totalCost
            description
            image
            thumbnail
          }
        }
      }
      ${Oem.fragments.OemBasicData}
    `,
  },
};
